import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { getCompanyInfo } from '../api';
import {
  COMPANY_UPDATED_EVENT,
  DEFAULT_COMPANY_NAME,
  getBrandInitial,
} from '../utils/brand';

const CompanyContext = createContext(null);

const EMPTY_COMPANY = {
  name: '',
  logo_url: null,
  logo_width: 220,
  logo_height: 75,
  contact: {},
};

export function CompanyProvider({ children }) {
  const [company, setCompany] = useState(EMPTY_COMPANY);
  const [loading, setLoading] = useState(true);

  const loadCompany = useCallback(async () => {
    try {
      const { data } = await getCompanyInfo();
      if (data) {
        setCompany((prev) => ({ ...prev, ...data }));
      }
    } catch {
      /* mantener valores por defecto */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCompany();
  }, [loadCompany]);

  useEffect(() => {
    const onUpdated = () => loadCompany();
    window.addEventListener(COMPANY_UPDATED_EVENT, onUpdated);
    return () => window.removeEventListener(COMPANY_UPDATED_EVENT, onUpdated);
  }, [loadCompany]);

  /** Sin fallback hasta terminar la carga: evita flash "DentalPro" antes del nombre en BD */
  const companyName = loading
    ? null
    : (company.name || '').trim() || DEFAULT_COMPANY_NAME;
  const brandInitial = companyName ? getBrandInitial(companyName) : '';

  useEffect(() => {
    const suffix = 'Insumos y equipamiento dental';
    if (loading) {
      document.title = suffix;
      return;
    }
    const n = (company.name || '').trim();
    document.title = n ? `${n} — ${suffix}` : suffix;
  }, [loading, company.name]);

  const value = useMemo(
    () => ({
      company,
      companyName,
      brandInitial,
      logoUrl: company.logo_url || null,
      loading,
      refreshCompany: loadCompany,
      notifyCompanyUpdated: () => window.dispatchEvent(new Event(COMPANY_UPDATED_EVENT)),
    }),
    [company, companyName, brandInitial, loading, loadCompany],
  );

  return <CompanyContext.Provider value={value}>{children}</CompanyContext.Provider>;
}

export function useCompany() {
  const ctx = useContext(CompanyContext);
  if (!ctx) {
    return {
      company: EMPTY_COMPANY,
      companyName: null,
      brandInitial: '',
      logoUrl: null,
      loading: true,
      refreshCompany: async () => {},
      notifyCompanyUpdated: () => {},
    };
  }
  return ctx;
}
